import {
  AlertTriangle,
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudMoon,
  CloudRain,
  CloudSnow,
  CloudSun,
  Droplets,
  Moon,
  Sun,
  Sunrise,
  Sunset,
  Thermometer,
  Umbrella,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useWarnings, useWeather } from '../../hooks/useWidgetsData';
import type { WeatherWarning } from '../../services/api';
import { compass, describeWeather, WIND_LABEL } from '../../services/weather';
import { useSettings } from '../../store/settings';

function iconFor(code: number, isDay = true): LucideIcon {
  if (code === 0) return isDay ? Sun : Moon;
  if (code <= 2) return isDay ? CloudSun : CloudMoon;
  if (code === 3) return Cloud;
  if (code === 45 || code === 48) return CloudFog;
  if (code >= 51 && code <= 57) return CloudDrizzle;
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return CloudRain;
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return CloudSnow;
  if (code >= 95) return CloudLightning;
  return Cloud;
}

const LEVEL_STYLE: Record<WeatherWarning['level'], string> = {
  red: 'border-red-500/70 bg-red-600/30',
  orange: 'border-orange-400/70 bg-orange-500/25',
  yellow: 'border-yellow-300/70 bg-yellow-400/20',
  unknown: 'border-[var(--border)] bg-white/5',
};

/** "2026-10-14T15:00" (location-local, from Open-Meteo) → "15:00" in the clock's format. */
function localTime(iso: string, hour12: boolean): string {
  const [, hm = ''] = iso.split('T');
  const [h = 0, m = 0] = hm.split(':').map(Number);
  if (!hour12) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${h % 12 || 12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`;
}

function Detail({ icon: Icon, children, label }: { icon: LucideIcon; children: ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5" title={label}>
      <Icon className="h-4 w-4 opacity-70" aria-hidden />
      <span className="sr-only">{label}:</span>
      {children}
    </span>
  );
}

export function Weather({ compact = false }: { compact?: boolean }) {
  const w = useSettings((s) => s.weather);
  const hour12 = useSettings((s) => s.clock.hour12);
  const { data, error, loading } = useWeather();
  const warnings = useWarnings();

  if (!w.location) {
    return <div className="glass muted p-4 text-sm">Set a location in Settings → Widgets → Weather to see the weather.</div>;
  }
  if (!data) {
    return <div className="glass muted p-4 text-sm">{loading ? 'Loading weather…' : (error ?? 'Weather unavailable')}</div>;
  }

  const Now = iconFor(data.current.code, data.current.isDay);
  const today = data.daily[0];
  const days = compact ? data.daily.slice(1, 4) : data.daily.slice(1, 7);
  const hours = data.hourly.slice(0, compact ? 6 : 12);
  const fmtDay = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
  const t = (n: number) => `${Math.round(n)}°`;
  const local = warnings.data?.local ?? [];
  const elsewhere = warnings.data?.elsewhere ?? 0;
  const country = w.location.name.split(',').pop()?.trim();

  const details = [
    w.showFeelsLike && (
      <Detail key="feels" icon={Thermometer} label="Feels like">
        Feels {t(data.current.feelsLike)}
      </Detail>
    ),
    w.showRain && today && (
      <Detail key="rain" icon={Umbrella} label="Chance of rain today">
        {today.rainChance}%
      </Detail>
    ),
    w.showWind && (
      <Detail key="wind" icon={Wind} label="Wind">
        {Math.round(data.current.wind)} {WIND_LABEL[w.windUnit]} {compass(data.current.windDirection)}
      </Detail>
    ),
    w.showHumidity && (
      <Detail key="hum" icon={Droplets} label="Humidity">
        {data.current.humidity}%
      </Detail>
    ),
    w.showUv && today && (
      <Detail key="uv" icon={Sun} label="UV index">
        UV {Math.round(today.uv)}
      </Detail>
    ),
    w.showSun && today && (
      <Detail key="sun" icon={Sunrise} label="Sunrise and sunset">
        {localTime(today.sunrise, hour12)} <Sunset className="ml-1 h-4 w-4 opacity-70" aria-hidden /> {localTime(today.sunset, hour12)}
      </Detail>
    ),
  ].filter(Boolean);

  return (
    <div className="glass space-y-3 p-4">
      <div className="flex items-center gap-4">
        <Now className="h-12 w-12 shrink-0" strokeWidth={1.5} aria-hidden />
        <div className="min-w-0">
          <div className="font-heading text-4xl font-light tabular-nums">{t(data.current.temperature)}</div>
          <div className="muted truncate text-sm">
            {describeWeather(data.current.code)} · {w.location.name.split(',')[0]}
          </div>
        </div>
        {today && (
          <div className="muted ml-auto text-right text-sm tabular-nums">
            H {t(today.max)} <br /> L {t(today.min)}
          </div>
        )}
      </div>

      {details.length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">{details}</div>}

      {w.showWarnings && local.length > 0 && (
        <ul className="space-y-1.5" aria-label="Weather warnings">
          {local.slice(0, compact ? 2 : 4).map((x) => (
            <li key={x.id} className={`flex items-start gap-2 rounded-lg border px-3 py-1.5 text-sm ${LEVEL_STYLE[x.level]}`}>
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span className="min-w-0">
                <span className="font-medium">{x.event}</span>
                <span className="muted block truncate text-xs">
                  {x.area}
                  {x.expires && ` · until ${new Date(x.expires).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12 })}`}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {w.showWarnings && elsewhere > 0 && (
        <p className="muted text-xs">
          {local.length === 0 ? 'No warnings for your area. ' : ''}
          {elsewhere} warning{elsewhere === 1 ? '' : 's'} elsewhere{country ? ` in ${country}` : ''} (MeteoAlarm)
        </p>
      )}

      {w.showHourly && hours.length > 0 && (
        <div className={`grid gap-1 text-center text-xs ${compact ? 'grid-cols-6' : 'grid-cols-12'}`} aria-label="Hourly forecast">
          {hours.map((h, i) => {
            const Icon = iconFor(h.code, h.isDay);
            return (
              <div key={h.time}>
                <div className="muted">{i === 0 ? 'Now' : localTime(h.time, hour12)}</div>
                <Icon className="mx-auto my-1 h-5 w-5" strokeWidth={1.5} aria-label={describeWeather(h.code)} />
                <div className="tabular-nums">{t(h.temperature)}</div>
                {w.showRain && <div className="tabular-nums text-sky-300">{h.rainChance >= 10 ? `${h.rainChance}%` : ' '}</div>}
              </div>
            );
          })}
        </div>
      )}

      {w.showDaily && (
        <div className={`grid gap-2 border-t border-[var(--border)] pt-3 text-center text-sm ${compact ? 'grid-cols-3' : 'grid-cols-6'}`}>
          {days.map((d) => {
            const Icon = iconFor(d.code);
            return (
              <div key={d.date}>
                <div className="muted">{fmtDay.format(new Date(`${d.date}T12:00:00`))}</div>
                <Icon className="mx-auto my-1 h-6 w-6" strokeWidth={1.5} aria-label={describeWeather(d.code)} />
                <div className="tabular-nums">
                  {t(d.max)} <span className="muted">{t(d.min)}</span>
                </div>
                {w.showRain && d.rainChance >= 10 && <div className="text-xs tabular-nums text-sky-300">{d.rainChance}%</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
